import parse from 'date-fns/parse';

import { RhinoAssetType } from '../compat';

import type { Asset } from './types';

type EpisodeLike = {
  episodeName?: string;
  episodeNumber?: number;
  seasonNumber?: number;
  type: 'episode' | 'program';
};

export function createTypeGuard<T extends Asset, R extends T>(...types: RhinoAssetType[]) {
  return function isAssetMatching(asset: T): asset is R {
    return types.includes(asset.type);
  };
}

export function getAssetEpisodeIndication<T extends EpisodeLike>(asset: T) {
  return {
    episodeName: asset.episodeName,
    episodeNumber: asset.episodeNumber,
    seasonNumber: asset.seasonNumber,
  };
}

export function isAvailableForPurchase(plan: string, now: number) {
  const [, startDate, endDate] = plan.split(';');

  if (!startDate || !endDate) {
    return false;
  }

  // 23/05/2023 00:00:00
  return (
    parse(startDate, 'dd/MM/yyyy HH:mm:ss', now).getTime() < now &&
    now < parse(endDate, 'dd/MM/yyyy HH:mm:ss', now).getTime()
  );
}

type PurchasableLike = {
  billingPlans?: string[];
  type: 'movie' | 'episode';
};

/**
 * @see https://kaltura.atlassian.net/browse/KUXEXT-22100
 */
export function isAssetPurchasable<T extends PurchasableLike>(asset: T): boolean {
  const now = Date.now();

  return Boolean(asset.billingPlans?.some((b) => isAvailableForPurchase(b, now)));
}

export type ContentMarker = NonNullable<ReturnType<typeof getAssetContentMarker>>;

type WithContentMarker = {
  contentMarkerText?: string;
  contentMarkerBackgroundColor?: string;
  contentMarkerTextColor?: string;
  type: 'movie' | 'episode' | 'series';
};

export function getAssetContentMarker<T extends WithContentMarker>(asset: T) {
  if (!asset.contentMarkerText) {
    return undefined;
  }

  return {
    backgroundColor: asset.contentMarkerBackgroundColor,
    text: asset.contentMarkerText,
    textColor: asset.contentMarkerTextColor,
  };
}
